/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo, PropsWithChildren, useRef, useEffect, useState, CSSProperties } from 'react';
import { observer } from 'mobx-react-lite';
import {
  useTable,
  useFlexLayout,
  useRowSelect,
  useSortBy,
  Row,
  IdType,
  useBlockLayout,
  Cell,
  usePagination,
} from 'react-table';
import { useSticky } from 'react-table-sticky';
import { VariableSizeList } from 'react-window';
import { useWindowSize } from 'hooks/useWindowSize';
import { useStore } from 'hooks/useRootStore';
import { Button } from 'components/Core/Button';
import { ReactComponent as ChevronIcon } from 'assets/chevron_icon.svg';
import { ReactComponent as ToFirstPageIcon } from 'assets/to_first_page_icon.svg';
import { TableProps } from './types';
import styles from './table.module.scss';

const paginationButtonStyle: CSSProperties = {
  height: 32,
  width: 42,
  border: '1px solid #2f5fe3',
  backgroundColor: 'white',
};
export const PAGE_SIZE = 50;
const HEIGTH_UNDER_TABLE = 92;

export const Table = observer(function <T>({
  columns,
  data,
  isFooter = false,
  isFullHeight = false,
  isSticky = false,
  simpleList = false,
  isPaginated = false,
  isManualSorting = false,
  addData,
  selectedRows,
  onSelectedRowsChange,
  defaultSortId,
  defaultSortOrder,
  apiSort,
}: PropsWithChildren<TableProps<T>>) {
  const {
    tableStore: { setStartPageIndex, startPageIndex },
  } = useStore();

  const windowSize = useWindowSize();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const listRef = useRef<VariableSizeList<any>>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rowHeights = useRef<any>({});

  const sortTypes = useMemo(
    () => ({
      caseInsensitive: (rowA: Row, rowB: Row, id: IdType<T>) => {
        const a = rowA.values[id].toLowerCase();
        const b = rowB.values[id].toLowerCase();
        return b > a ? -1 : b < a ? 1 : 0;
      },
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    gotoPage,
    previousPage,
    nextPage,
    canNextPage,
    canPreviousPage,
    // pageCount,
    pageOptions,
    prepareRow,
    state: { selectedRowIds, pageIndex, sortBy },
    footerGroups,
  } = useTable<Partial<T>>(
    {
      columns,
      data,
      sortTypes,
      manualSortBy: isManualSorting,
      initialState: {
        selectedRowIds: selectedRows || {},
        pageSize: PAGE_SIZE,
        pageIndex: startPageIndex || 0,
        sortBy: defaultSortId ? [{ id: defaultSortId, desc: defaultSortOrder }] : [],
      },
    },
    isSticky ? useBlockLayout : useFlexLayout,
    useSortBy,
    usePagination,
    useRowSelect,
    useSticky
  );

  useEffect(() => {
    if (isManualSorting) {
      const column = { ...headerGroups[0].headers.find(({ id }) => id === sortBy[0]?.id) };
      if (column && column.canSort && column.sortType === 'apiSort') {
        apiSort && apiSort(sortBy[0].id, sortBy[0].desc ? 'DESC' : 'ASC');
      } else defaultSortId && apiSort && apiSort();
    }
  }, [sortBy]);

  useEffect(() => {
    onSelectedRowsChange && onSelectedRowsChange(selectedRowIds);
  }, [selectedRowIds]);

  const setRowHeight = useCallback(
    (index, size) => {
      if (listRef.current) listRef.current.resetAfterIndex(0);
      rowHeights.current = { ...rowHeights.current, [index]: size };
    },
    [windowSize]
  );

  const getRowHeight = useCallback(
    (index): number => (rowHeights.current ? rowHeights.current[index] || 40 : 40),
    [windowSize]
  );

  const RenderRow = useCallback(
    ({ index, style }) => {
      const rowRef = useRef<HTMLDivElement>(null);
      useEffect(() => {
        if (rowRef.current) {
          setRowHeight(index, rowRef.current.clientHeight);
        }
        // eslint-disable-next-line
      }, [rowRef]);
      const row = rows[index];
      prepareRow(row);
      const isSelected =
        'id' in row.original && (row.original as any).id && selectedRows && selectedRows[(row.original as any).id];
      return (
        <div
          ref={rowRef}
          className={`${styles.row}  ${isSelected && styles.selected}`}
          {...row.getRowProps({
            style,
          })}>
          {row.cells.map(renderCell)}
        </div>
      );
    },
    [prepareRow, rows, setRowHeight]
  );

  const RenderRow2 = useCallback(
    (row: Row<Partial<T>>) => {
      prepareRow(row);
      const rowProps = row.getRowProps();
      return (
        <div {...rowProps} className={`${styles.row} ${isFullHeight && styles.noScroll}`}>
          {row.cells.map(renderCell)}
        </div>
      );
    },
    [prepareRow, rows, page, setRowHeight]
  );

  const renderCell = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (cell: Cell<Partial<T>, any>) => {
      return (
        <div {...cell.getCellProps()} className={`${styles.cell} ${styles[cell.column.id]}`}>
          {cell.render('Cell')}
        </div>
      );
    },
    []
  );

  const [tableBodyHeight, setTableBodyHeight] = useState(220);

  useEffect(() => {
    const rowsHeightArr: number[] = Object.values(rowHeights.current).slice(0, rows.length) as number[];
    const rowsHeight = rowsHeightArr.length ? rowsHeightArr.reduce((a, b) => a + b) + 1 * rowsHeightArr.length : 0;
    const top = document.getElementById('table-header')?.getBoundingClientRect().bottom || 0;
    const bodyHeight = (windowSize?.wHeight || window.innerHeight) - top - HEIGTH_UNDER_TABLE;
    setTableBodyHeight(
      isSticky || simpleList
        ? bodyHeight
        : isFullHeight
        ? rowsHeight
        : rowsHeight > bodyHeight
        ? bodyHeight
        : rowsHeight
    );
  }, [windowSize, rows, rowHeights.current, windowSize?.wHeight]);

  const [rerender, setRerender] = useState(false);
  const [scrollBarWidth, setScrollBarWidth] = useState(0);

  useEffect(() => {
    setRerender(!rerender);
  }, [rows, page]);

  useEffect(() => {
    const el = document.getElementById('tableBody');
    if (el) {
      if (simpleList) {
        setScrollBarWidth(el.offsetWidth - el.clientWidth);
      } else {
        const child = el.firstChild as HTMLElement;
        if (child) setScrollBarWidth(child.offsetWidth - child.clientWidth);
      }
    }
  }, [rerender]);

  useEffect(() => {
    if (isPaginated) {
      setStartPageIndex(pageIndex);
      pageIndex === pageOptions.length - 1 && addData && addData((pageIndex + 1) * PAGE_SIZE);
      const el = document.getElementById('tableBody')?.firstChild as HTMLElement;
      el && el.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [pageIndex]);

  return (
    <>
      <div {...getTableProps()} className={`${styles.table} ${isSticky && styles.sticky}`} id='table'>
        <div className={styles.header} id='table-header'>
          {headerGroups.map((headerGroup) => (
            <div {...headerGroup.getHeaderGroupProps()} className={styles.columnHeaderGroup}>
              {headerGroup.headers.map((column) => (
                <div
                  {...column.getHeaderProps()}
                  className={`${styles.columnHeader} ${styles[column.id]}`}
                  id={styles[column.id]}>
                  <div {...column.getSortByToggleProps()} className={`${styles.headerTitle}`}>
                    {column.render('Header')}&nbsp;
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <ChevronIcon className={`${styles.sortIcon}`} />
                      ) : (
                        <ChevronIcon className={`${styles.sortIcon} ${styles.ascSort}`} />
                      )
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
        {isSticky || simpleList ? (
          <div
            {...getTableBodyProps()}
            id='tableBody'
            className={`${styles.body} ${simpleList && styles.simpleList}`}
            style={{
              height: tableBodyHeight,
              width: `calc(100% + ${scrollBarWidth}px)`,
            }}>
            {isPaginated ? page.map(RenderRow2) : rows.map(RenderRow2)}
          </div>
        ) : (
          <div
            {...getTableBodyProps()}
            style={{ width: `calc(100% + ${scrollBarWidth}px)` }}
            id='tableBody'
            className={styles.body}>
            <VariableSizeList
              className={`${styles.tableRows} ${isFullHeight && styles.noScroll}`}
              height={tableBodyHeight}
              itemCount={isPaginated ? page.length : rows.length}
              itemSize={getRowHeight}
              width='100%'
              ref={listRef}>
              {RenderRow}
            </VariableSizeList>
          </div>
        )}
        {isFooter && rows.length > 0 && (
          <div>
            {footerGroups.map((footerGroup) => (
              <div {...footerGroup.getFooterGroupProps()} className={styles.footerRow}>
                {footerGroup.headers.map((column) => (
                  <div {...column.getFooterProps()} className={styles.footerCell}>
                    {column.render('Footer')}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
      {isPaginated && (
        <div className={styles.paginateButtons}>
          <Button
            title=''
            icon={<ToFirstPageIcon style={{ margin: 0 }} />}
            onClick={() => gotoPage(0)}
            isDisabled={!canPreviousPage}
            stylesProp={{ ...paginationButtonStyle, width: 20 }}
          />
          <Button
            title=''
            icon={<ChevronIcon style={{ transform: 'rotate(90deg)', margin: 0 }} />}
            onClick={() => previousPage()}
            isDisabled={!canPreviousPage}
            stylesProp={paginationButtonStyle}
          />
          <Button
            title='Next'
            icon={<ChevronIcon className={styles.nextIcon} />}
            onClick={() => nextPage()}
            isDisabled={!canNextPage}
            stylesProp={{
              ...paginationButtonStyle,
              height: 32,
              width: 155,
              flexDirection: 'row-reverse',
              alignItems: 'center',
              textTransform: 'uppercase',
              fontSize: 10,
              letterSpacing: 1,
              lineHeight: '16px',
              color: '#2f5fe3',
            }}
          />
          <span className={styles.paginateText}>
            Page <strong>{pageIndex + 1}</strong>{' '}
          </span>
        </div>
      )}
    </>
  );
});
